import { Component, HostListener, OnDestroy, Output, EventEmitter, Input, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { APP_CONST } from "src/app/shared/constants/app-constants";
import { Router, ActivatedRoute, RouterLink, NavigationEnd } from '@angular/router';
import { Observable, Subscription } from "rxjs";
import { filter as rxFilter } from 'rxjs/operators';
import { HelpersService } from "../../services/helpers/helpers.service";
import * as _ from 'lodash';
import { NgClass, NgIf, NgFor } from '@angular/common';

@Component({
  selector: 'app-side-bar-v2',
  templateUrl: './side-bar-v2.component.html',
  styleUrls: ['./side-bar-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgIf,
    NgFor,
    RouterLink,
  ]
})
export class SideBarV2Component implements OnInit, OnDestroy {
  // Side-bar component properties
  @Output() sidebarWidthChange = new EventEmitter<boolean>();
  currentUrl: string;
  isSidebarOpen = false;
  isSidebarClosed = false;
  isInProjectSection = false;
  brandDetails: any = [];
  brandId: string | null = null;
  routeLinks = APP_CONST.ROUTES_ACCOUNTS;
  accountId!: string;
  project_id: string | null = null;
  activePath: string = 'home';
  activeItem: any = 'home';
  isSidebarHovered = false;
  routeSubscription!: Subscription;
  isSidebarExpanded!: boolean;
  itemsVisible: boolean = false;
  menuItems: any = [];

  // Menu-item component properties
  @Input() menuItemsInput; // Array of all menu items for managing active state
  @Input() item;
  @Input() openSideBarInput;
  @Input() isSidebarOpenInput = false;
  @Input() isMainMenu = false;
  @Input() activePathInput: string = '';
  @Input() isActive: boolean = false; 
  @Input() activeItemInput: any;
  @Output() setActive = new EventEmitter<any>();
  @Input() isSidebarHoveredInput: boolean = false;
  @Output() activate = new EventEmitter<string>();
  @Input() isSidebarExpandedInput: boolean = false; 
  // @Input() itemsVisibleInput: boolean = false;
  accountIdInput: string = '';
  projectIdInput: string = '';
  isHovered!: boolean;
  private hasSetInitialActiveItem = false;
  private routeChangeSubscription!: Subscription;
  /** Rebuild menu list only when account/project scope changes — not on `active_tab` etc. */
  private lastMenuScopeKey = '';
  /** Skip sidebar icon refresh when only the query string changes on the same route. */
  private lastSyncedUrlPath = '';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private helperService: HelpersService,
    private cdr: ChangeDetectorRef
  ) {
    this.openSideBar = this.openSideBar.bind(this);
    this.currentUrl = this.router.url;
  }

  ngOnInit() {
    this.lastSyncedUrlPath = this.router.url.split('?')[0];

    // Side-bar initialization
    this.route.queryParams.subscribe(params => {
      this.brandId = params['project_id'];
      this.accountId = params['account_id'];

      const scopeKey = `${this.accountId ?? ''}|${this.brandId ?? ''}`;
      const menuScopeChanged = scopeKey !== this.lastMenuScopeKey;
      if (menuScopeChanged) {
        this.lastMenuScopeKey = scopeKey;
        if (this.brandId) {
          this.menuItems = this.helperService.getActiveSideMenu(false, this.accountId);
        } else {
          this.menuItems = this.helperService.getActiveSideMenu(true, this.accountId);
        }
      }

      let url = this.router.url;
      const routeData = this.helperService.prepareRouteData(url);

      // Only set the active item on first load (or when account/project scope changes)
      if (!this.hasSetInitialActiveItem || menuScopeChanged) {
        if (routeData && routeData.routeName) {
          const basePath = routeData.routeName;
          const subPath = routeData.subRouteName ? basePath + '/' + routeData.subRouteName : basePath;
          // Prefer submenu path if it exists; fallback to base path
          const activeSubMenu = this.findMenuItemByPath(subPath);
          const activeTopMenu = _.find(this.menuItems, { path: basePath });

          if (activeSubMenu && !_.isEmpty(activeSubMenu)) {
            this.setActiveItem(subPath);
            const parentMenu = this.findParentMenuBySubPath(subPath);
            if (parentMenu) {
              parentMenu.showSubMenu = true;
            }
          } else if (activeTopMenu && !_.isEmpty(activeTopMenu)) {
            this.setActiveItem(basePath);
          } else if (this.menuItems && this.menuItems.length) {
            this.setActiveItem(this.menuItems[0].path);
          }
        } else if (this.menuItems && this.menuItems.length) {
          this.setActiveItem(this.menuItems[0].path);
        }
        this.hasSetInitialActiveItem = true;
        this.cdr.markForCheck();
      }
    });

    // Menu-item initialization
    this.getQueryParamFromURL();

    // Path changes only — ignore query-only updates (e.g. Sentiment `active_tab`)
    this.routeChangeSubscription = this.router.events
      .pipe(rxFilter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const pathOnly = event.urlAfterRedirects.split('?')[0];
        if (pathOnly === this.lastSyncedUrlPath) {
          return;
        }
        this.lastSyncedUrlPath = pathOnly;
        this.syncActiveFromCurrentUrl();
      });
  }

  ngOnDestroy(): void {
    if (this.routeChangeSubscription) {
      this.routeChangeSubscription.unsubscribe();
    }
  }

  // Menu-item methods
  getQueryParamFromURL() {
    this.route.queryParams.subscribe((params) => {
      this.accountIdInput = params['account_id'] || '';
      this.projectIdInput = params['project_id'] || '';
    });
  }

  /**
   * Handles submenu toggling for a specific menu item
   */
  toggleSubMenuForItem(menuItem: any) {
    if (menuItem && menuItem.submenu) {
      // Close all other submenus at this level
      if (this.menuItems) {
        this.menuItems.forEach((menu) => {
          if (menu !== menuItem && menu.submenu) {
            menu.showSubMenu = false;
          }
        });
      }
      // Toggle this submenu
      menuItem.showSubMenu = !menuItem.showSubMenu;
      // If closing, also close all nested submenus
      if (!menuItem.showSubMenu) {
        this.resetSubMenuState(menuItem.submenu);
      }
    }
  }

  /**
   * Recursively closes all submenus in a given list
   */
  resetSubMenuState(items): void {
    items.forEach((item) => {
      item.showSubMenu = false;

      // Recursively reset showSubMenu for nested submenus
      if (item.submenu) {
        this.resetSubMenuState(item.submenu);
      }
    });
  }

  getQueryParams(firstTab?: any) {
    const queryParams: any = {}
    if (this.accountIdInput) queryParams.account_id = this.accountIdInput
    if (this.projectIdInput) queryParams.project_id = this.projectIdInput
    if (firstTab && firstTab != '') queryParams.active_tab = firstTab
    return queryParams
  }

  /**
   * Handles click on a menu item (without submenu)
   */
  onItemClick(path: string): void {
    this.setActiveItem(path);
    this.setActive.emit(path);
    this.isActive = this.isItemActive(path); 
    // Ensure data-bearing routes like inbox trigger data load by marking app state as not pending
    if (!path.startsWith('settings')) {
      try { this.helperService.setPendingState(false); } catch {}
    }
    this.cdr.detectChanges();
  }

  onRedirectClick(menuItem: any): void {
    if (menuItem.redirection_url && menuItem.redirection_url != '' && menuItem.redirection_url != null) {
      window.open(menuItem.redirection_url, '_blank');
    }
  }

  /**
   * Checks if the given path is the currently active path
   */
  isItemActive(path: string): boolean {
    return path === this.activePath;
  }

  // Returns true if any submenu of the given menuItem is active
  isAnySubmenuActive(menuItem: any): boolean {
    if (!menuItem.submenu) return false;
    return menuItem.submenu.some(subItem => this.isItemActive(subItem.path));
  }

  // Side-bar methods
  // Listen to mouseenter to expand the sidebar
  @HostListener('mouseenter', ['$event'])
  onSidebarHoverEnter(event: MouseEvent) {
    this.isSidebarHovered = true;
    this.itemsVisible = true;
    this.expandSidebar();
  }

  // Listen to mouseleave to collapse the sidebar
  @HostListener('mouseleave', ['$event'])
  onSidebarHoverLeave(event: MouseEvent) {
    this.isSidebarHovered = false;
    this.itemsVisible = false;
    this.collapseSidebar();
  }

  // Helper: Find parent menu for a given submenu path
  findParentMenuBySubPath(subPath: string): any {
    return this.menuItems.find(menuItem => menuItem.submenu && menuItem.submenu.some(subItem => subItem.path === subPath));
  }

  // Helper: Find a menu (top-level or nested) by its path
  findMenuItemByPath(path: string): any {
    if (!this.menuItems || !this.menuItems.length) return null;
    const direct = this.menuItems.find((menu: any) => menu.path === path);
    if (direct) return direct;

    const stack: any[] = [];
    this.menuItems.forEach((menu: any) => {
      if (menu && menu.submenu && Array.isArray(menu.submenu)) {
        stack.push(...menu.submenu);
      }
    });

    while (stack.length) {
      const item: any = stack.shift();
      if (item && item.path === path) return item;
      if (item && item.submenu && Array.isArray(item.submenu)) {
        stack.push(...item.submenu);
      }
    }
    return null;
  }

  // Expand sidebar
  expandSidebar() {
    this.isSidebarExpanded = true;
    this.updateSidebarWidth();
    // Open submenu if activePath is a submenu
    const parentMenu = this.findParentMenuBySubPath(this.activePath);
    if (parentMenu) {
      parentMenu.showSubMenu = true;
    }
    // this.showItemsWithDelay();
  }

  showItemsWithDelay() {
    setTimeout(() => {
      this.itemsVisible = true;
    }, 200);
  }

  // Collapse sidebar
  collapseSidebar() {
    this.isSidebarExpanded = false;
    this.itemsVisible = false;
    this.updateSidebarWidth();
  }

  // Update the sidebar width dynamically
  updateSidebarWidth() {
    this.sidebarWidthChange.emit(this.isSidebarExpanded);
  }

  openSideBar(event: any) {
    this.isSidebarOpen = true
    if (!event?.currentTarget?.contains(event.relatedTarget)) {
      const element = document.getElementById("side-bar");
      if (element?.classList) element.classList.remove('side-bar-closed')

      const logoEle = document.getElementById('logo')
      if (logoEle?.classList) logoEle.classList.remove('side-bar-closed-logo')
    }
  }

  // Build absolute router link to ensure navigation always targets the correct outlet
  getAbsolutePath(path: string): any[] | string {
    if (!path) return ['/'];
    const routeData = this.helperService.prepareRouteData(this.router.url);
    // If already absolute, let router handle
    if (path.startsWith('/')) return path;

    // Split provided path into segments to avoid encoded '/'
    const rawSegments = path.split('/').filter(Boolean);

    if (routeData?.level === 'project') {
      // Remove optional 'project' leading segment if present
      const segments = (rawSegments[0] === 'project') ? rawSegments.slice(1) : rawSegments;
      return ['/account/project', ...segments];
    }

    // Account level base
    return ['/accounts', ...rawSegments];
  }

  setActiveItem(path: string): void {
    this.activePath = path;
    this.activeItem = path;
  }

  trackByMenuTabKey(_index: number, menuItem: { tabKey?: string; path?: string }): string {
    return String(menuItem?.tabKey ?? menuItem?.path ?? _index);
  }

  // Sync sidebar active state based on current URL (handles manual URL changes)
  private syncActiveFromCurrentUrl(): void {
    const url = this.router.url;
    const routeData = this.helperService.prepareRouteData(url);
    if (!routeData || !routeData.routeName) return;

    const basePath = routeData.routeName;
    const subPath = routeData.subRouteName ? basePath + '/' + routeData.subRouteName : basePath;

    const activeSubMenu = this.findMenuItemByPath(subPath);
    const activeTopMenu = _.find(this.menuItems, { path: basePath });

    const prevPath = this.activePath;
    if (activeSubMenu && !_.isEmpty(activeSubMenu)) {
      this.setActiveItem(subPath);
      const parentMenu = this.findParentMenuBySubPath(subPath);
      if (parentMenu) parentMenu.showSubMenu = true;
    } else if (activeTopMenu && !_.isEmpty(activeTopMenu)) {
      this.setActiveItem(basePath);
    }
    if (this.activePath !== prevPath) {
      this.cdr.markForCheck();
    }
  }
}
